import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Cookie, X } from '@phosphor-icons/react';
import { PUBLIC_PATHS, isPublicPath } from '../utils/publicPaths';

export { PUBLIC_PATHS, isPublicPath };

const COOKIE_CONSENT_KEY = 'valulearn_cookie_consent';

interface CookieConsent {
  consented: boolean;
  timestamp: number;
  version: string;
}

interface CookieConsentBannerProps {
  onConsentChange?: (consented: boolean) => void;
}

export const getCookieConsent = (): CookieConsent | null => {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

export const hasUserConsented = (): boolean => {
  const consent = getCookieConsent();
  return consent?.consented === true;
};

export const resetCookieConsent = (): void => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  window.dispatchEvent(new CustomEvent('cookieConsentReset'));
};


const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({ onConsentChange }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const location = useLocation();

  // Check if current path is allowed for showing the banner
  const isAllowedPath = isPublicPath(location.pathname);

  useEffect(() => {
    // Only show on allowed paths
    if (!isAllowedPath) {
      setShowBanner(false);
      setIsAnimating(false);
      return;
    }

    // Check if user has already made a choice
    const consent = getCookieConsent();
    if (!consent) {
      // Delay showing banner for better UX
      const timer = setTimeout(() => {
        setShowBanner(true);
        setTimeout(() => setIsAnimating(true), 50);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isAllowedPath]);

  // Listen for reset events (from Cookie Settings in footer)
  useEffect(() => {
    const handleReset = () => {
      // Only show on reset if on allowed path
      if (isAllowedPath) {
        setShowBanner(true);
        setTimeout(() => setIsAnimating(true), 50);
      }
    };
    window.addEventListener('cookieConsentReset', handleReset);
    return () => window.removeEventListener('cookieConsentReset', handleReset);
  }, [isAllowedPath]);

  const saveConsent = (consented: boolean) => {
    const consent: CookieConsent = {
      consented,
      timestamp: Date.now(),
      version: '1.0'
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    onConsentChange?.(consented);

    if (consented) {
      window.dispatchEvent(new CustomEvent('cookieConsentGranted'));
    }

    setIsAnimating(false);
    setTimeout(() => setShowBanner(false), 300);
  };

  const handleAccept = () => saveConsent(true);
  const handleReject = () => saveConsent(false);

  if (!showBanner) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 transition-all duration-300 ease-out ${
        isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
    >
      <div className="max-w-4xl mx-auto">
        <div className="bg-white/95 dark:bg-grey-darker/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-grey-very-light dark:border-grey p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Icon and Text */}
            <div className="flex items-start gap-3 flex-1">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-extra-light dark:bg-blue/20 flex items-center justify-center">
                <Cookie weight="duotone" className="w-5 h-5 text-blue-darker dark:text-blue" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-grey-darker dark:text-white mb-1">
                  We value your privacy
                </h3>
                <p className="text-sm text-grey dark:text-grey-light">
                  We use cookies to enhance your learning experience and analyze site usage.{' '}
                  <Link
                    to="/privacy"
                    className="text-blue hover:text-blue-darker dark:hover:text-blue-extra-light underline"
                  >
                    Learn more
                  </Link>
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                onClick={handleReject}
                className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-grey dark:text-grey-light border border-grey-light dark:border-grey rounded-full hover:bg-grey-extra-light dark:hover:bg-grey transition-colors"
              >
                Reject
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 sm:flex-none px-6 py-2 text-sm font-medium text-white bg-blue-darker hover:bg-blue rounded-full transition-colors"
              >
                Accept
              </button>
            </div>

            {/* Close button (mobile) */}
            <button
              onClick={handleReject}
              className="absolute top-3 right-3 sm:hidden p-1 text-grey hover:text-grey-darker dark:text-grey-light dark:hover:text-white transition-colors"
              aria-label="Close"
            >
              <X weight="bold" className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
